import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { Doughnut } from 'react-chartjs-2';
import { CustomSelect } from 'components/CustomForm';
import CommonTable from 'components/CommonTable';
import { device } from 'helpers/screen';
import { Card, EmptyLabel } from 'components/CommonWidgets';
import { getInputs } from 'network/apis/CommonApi';
import { getStringColor, isBlank } from 'helpers/strings';
import Skeleton from 'react-loading-skeleton';
import { useQuery } from 'react-query';
import {
	getBuysReportSum,
	getDistsReportSum,
	getProductsReportSum,
} from 'network/apis/ReportingApi';
import { getProvinces } from "network/apis/InformationApi";
import { DefaultProvinceFilter, DefaultInputFilter, DefaultMonthFilter } from 'redux/global.consts';
import { PersianDate } from "helpers/dates";
import { DashboardContext } from 'context/DashboardContext';

const barOptions = {
	plugins: {
		title: {
			display: false,
			text: ''
		},
		legend: {
			position: 'right'
		}
	},
	responsive: false,
	maintainAspectRatio: false,
	scales: {
		xAxes: [
			{
				gridLines: {
					display: false,
					drawBorder: false
				}
			}
		],
		yAxes: [
			{
				gridLines: {
					display: false,
					drawBorder: false
				}
			}
		]
	}
};

const getInputLabel = (item, isProductTab) => {
	if (isProductTab) {
		return item?.['food_item']?.name ?? '-';
	} else {
		return item?.input?.name || item?.input || '-';
	}
};

const getChartData = (data, isProductTab) => {
	const items = data?.list?.data?.items;

	return {
		labels: items?.map((item) => getInputLabel(item, isProductTab)) ?? [],
		datasets: [
			{
				label: '# of Votes',
				data: items?.map((item) => item?.sum) ?? [],
				backgroundColor: items?.map(
					(item) => getStringColor(getInputLabel(item, isProductTab))
				) ?? [],
			}
		],
	};
};

const ChartCard = styled.div`
	margin-top: 1rem;
	background-color: white;
	border-radius: 8px;
	box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
	padding: 1rem 1rem 2rem 1rem;
	width: 100%;
`;

const ChartWrapper = styled.div`
	position: relative;

	@media ${device.tablet} {
		padding: 5.1% 0 0 4%;
	}
	@media ${device.laptopSM} {
		padding: 10% 4% 6% 0;
		transform: scale(0.8);
	}
	@media ${device.laptop} {
		padding: 10% 0 6% 4%;
		transform: scale(1.2);
	}
	@media ${device.laptopL} {
		padding: 10% 0 6% 8%;
		transform: scale(1.3);
	}
	@media (min-width: 1500px) {
		padding: 10% 0 6.5% 11%;
		transform: scale(1.3);
	}
	@media (min-width: 1600px) {
		padding: 8.5% 0 6.5% 14%;
		transform: scale(1.3);
	}
	@media (min-width: 1700px) {
		padding: 8.5% 0 6.5% 16%;
		transform: scale(1.4);
	}
	@media (min-width: 1750px) {
		padding: 8.5% 0 6.5% 18%;
		transform: scale(1.4);
	}
	@media ${device.desktop} {
		padding: 8.5% 0 6.5% 21%;
		transform: scale(1.5);
	}
`;

const getReportVerb = (reportType) => {
	if (reportType === 'distribute') {
		return 'توزیع';
	}

	return 'خرید';
};

const getReportMatter = (reportType) => {
	if (reportType === 'product') {
		return 'ترکیب';
	}

	return 'نهاده';
};

const getReportEndpoint = (reportType) => {
	switch (reportType) {
		case 'buy':
			return getBuysReportSum;
			break;
		case 'distribute':
			return getDistsReportSum;
			break;
		case 'product':
			return getProductsReportSum;
		default:
			return () => { };
	}
};

///reportType is an enum -> buy|distribute|product, default: buy
const ReportChart = ({ reportType = 'buy' }) => {
	const [selectedInput, setSelectedInput] = useState(DefaultInputFilter);
	const [selectedProvince, setSelectedProvince] = useState(DefaultProvinceFilter);
	const [selectedMonth, setSelectedMonth] = useState();
	const [selectedYear, setSelectedYear] = useState();
	const [filterQuery, setFilterQuery] = useState();

	const { currentTab } = useContext(DashboardContext);

	useEffect(() => {
		let filterQuery = '?';

		if ((selectedInput?.value ?? 0) > 0) {
			const queryKey = reportType === 'product' ? 'food_item_id' : 'input_id';
			filterQuery += `${queryKey}=${selectedInput.value}&`;
		}
		if ((selectedProvince?.value ?? 0) > 0) {
			filterQuery += `province_id=${selectedProvince.value}&`;
		}
		if ((selectedMonth?.value ?? 0) > 0) {
			filterQuery += `month=${selectedMonth.value}&`;
		}
		if ((selectedYear?.value ?? 0) > 0) {
			filterQuery += `year=${selectedYear.value}&`;
		}

		setFilterQuery(filterQuery);
	}, [selectedInput, selectedProvince, selectedMonth, selectedYear, setFilterQuery]);

	const { data, isLoading } = useQuery([`get-${reportType}-chart-data`, filterQuery],
		() => getReportEndpoint(reportType)(filterQuery),
		{
			refetchOnWindowFocus: false,
			enabled: currentTab === reportType,
		}
	);

	const getItems = () => data?.list?.data?.items ?? [];

	const isEmpty = () => {
		const items = reportType === 'product' ?
			getItems().flatMap((node) => node.items) : getItems();

		return !isLoading && items.length < 1;
	};

	const { data: inputsData } = useQuery(
		["get-inputs"],
		() => getInputs(),
		{
			refetchOnWindowFocus: false,
			enabled: currentTab === reportType,
		}
	);

	const getInputOptions = () => {
		if (inputsData?.data) {
			const inputOptions = inputsData.data.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			});

			return [DefaultInputFilter, ...inputOptions];
		}

		return [];
	};

	const { data: provincesData } = useQuery(
		["get-provinces"],
		() => getProvinces(),
		{
			refetchOnWindowFocus: false,
			enabled: currentTab === reportType,
		}
	);

	const getProvinceOptions = () => {
		if (provincesData?.data) {
			const provinceOptions = provincesData.data.map((province, index) => {
				return { value: province?.province_id, label: province?.name };
			});

			return [DefaultProvinceFilter, ...provinceOptions];
		}

		return [];
	};

	const getMonthOptions = () => {
		const monthOptions = new PersianDate().getMonthNames().map((month, index) => {
			return { value: index + 1, label: month };
		});

		return [DefaultMonthFilter, ...monthOptions];
	};

	const getYearOptions = () => {
		const currentYear = new PersianDate().getYear();
		return [`${currentYear - 1}`, currentYear].map((year) => {
			return { value: year, label: year };
		});
	};

	return (
		<Card
			borderRadius={reportType === 'product' ? '26px' : '8px'}
			cardHeight={reportType === 'product' ? '100%' : '95%'}
			mTop={reportType === 'product' ? '1rem' : '2.9rem'}
		>
			<div className="mb-2 text-end">گزارش {getReportVerb(reportType)} استانی</div>
			<Row className="d-flex justify-content-between mx-1">
				<Col md={6} className='px-1'>
					<CustomSelect
						placeholder={getReportMatter(reportType)}
						radius="14"
						options={getInputOptions()}
						onChange={setSelectedInput}
						value={selectedInput}
						menuPosition='fixed'
					/>
				</Col>
				<Col md={6} className='px-1'>
					<CustomSelect
						placeholder="استان"
						radius="14"
						options={getProvinceOptions()}
						onChange={setSelectedProvince}
						value={selectedProvince}
						menuPosition='fixed'
					/>
				</Col>
			</Row>
			<Row className="d-flex justify-content-between mt-2 mx-1">
				<Col md={6} className='px-1'>
					<CustomSelect
						placeholder="سال"
						radius="14"
						options={getYearOptions()}
						onChange={setSelectedYear}
						value={selectedYear}
						menuPosition='fixed'
					/>
				</Col>
				<Col md={6} className='px-1'>
					<CustomSelect
						placeholder="ماه"
						radius="14"
						options={getMonthOptions()}
						onChange={setSelectedMonth}
						value={selectedMonth}
						menuPosition='fixed'
					/>
				</Col>
			</Row>
			<ChartCard>
				{isLoading ? (<Skeleton count={1} height={220} />) : (
					isEmpty() ? (
						<EmptyLabel>
							رکوردی ثبت نشده است
						</EmptyLabel>
					) : (
						<ChartWrapper>
							<Doughnut
								data={getChartData(data, reportType === 'product')}
								options={barOptions}
							/>
						</ChartWrapper>
					)
				)}
			</ChartCard>
			<ChartCard>
				{isLoading ? (<Skeleton count={7} />) : (
					isEmpty() ? (
						<EmptyLabel>
							رکوردی ثبت نشده است
						</EmptyLabel>
					) : (
						<CommonTable
							cols={['مقدار (تن)', `نوع ${getReportMatter(reportType)}`]}
							tablewidth="100%"
							maxHeight='14rem'
						>
							{getItems().map((item, index) => (
								<tr key={index}>
									<td>{`${item?.sum ?? 0}`.persianCurrency()}</td>
									<td>
										{getInputLabel(item, reportType === 'product')}
									</td>
								</tr>
							))}
						</CommonTable>
					)
				)}
			</ChartCard>
		</Card>
	);
};

export default ReportChart;
